import type { BaseComponentProps } from '@coursera/cds-common';
import { useLocalizedStringFormatter } from '@coursera/cds-common';

import CardBodyContent from '@core/cards/common/CardBodyContent';
import { useCardContext } from '@core/cards/common/CardContext';
import i18nMessages from '@core/cards/common/i18n';
import Typography from '@core/Typography2';

type Props = {
  /**
   * list of skills to show
   */
  skills: string[];

  /**
   * Custom string to use as the prefix label.
   * Make sure to handle localization of this string when passing in.
   *
   * @default "Skills you'll gain"
   **/
  prefix?: string;
} & BaseComponentProps<'div'>;

/**
 * Common component to render a list of skills gained for a product
 */
const CardSkills = (props: Props) => {
  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  const {
    skills,
    prefix = stringFormatter.format('skills_prefix'),
    ...rest
  } = props;

  const context = useCardContext();
  const useCompactTypography = context?.useCompactTypography;
  const isTest1 =
    context?.experimentVariant === 'test1' ||
    context?.experimentVariant === 'test1.1' ||
    context?.experimentVariant === 'test1.2' ||
    context?.experimentVariant === 'test1.3' ||
    context?.experimentVariant === 'test1.4' ||
    context?.experimentVariant === 'test1.5';

  const typographyVariant = useCompactTypography
    ? 'bodyTertiary'
    : 'bodySecondary';

  const skillsList = skills.join(', ');
  return (
    <CardBodyContent {...rest}>
      <Typography color="supportText" component="p" variant={typographyVariant}>
        <Typography
          color={isTest1 ? 'supportText' : 'body'}
          component={isTest1 ? 'span' : 'strong'}
          variant={typographyVariant}
        >
          {prefix}
          {': '}
        </Typography>
        {skillsList}
      </Typography>
    </CardBodyContent>
  );
};

export default CardSkills;
